import { AddCircleOutline as AddCircleOutlineIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import {
  ReactElement,
  Suspense,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import {
  deletePreference,
  getPreferences,
  postPreference,
} from '@app/adapter/user-service';
import { ResultItemCard } from '@app/components/Product/ResultItemCard';
import { SearchCondition } from '@app/components/Search/SearchCondition';
import { SearchNoResult } from '@app/components/Search/SearchNoResult';
import { SearchTypeTab } from '@app/components/Search/SearchTypeTab';
import { BottomMenu } from '@app/components/Shared/BottomMenu';
import {
  getProductsDescription,
  getProductsKeywords,
  getProductsTitle,
  HeadBlock,
} from '@app/components/Shared/HeadBlock';
import { Loading } from '@app/components/Shared/Loading';
import { ScrollThreshold } from '@app/components/Shared/ScrollThreshold';
import { userAuthInfoSelector } from '@app/domain/app';
import {
  attributesByClinicalDepartmentSelector,
  attributesByJobTypeSelector,
  categorySelector,
  locationsByPrefectureSelector,
  searchConditionState,
  searchResultProductsState,
} from '@app/domain/catalog';
import { getProducts } from '@app/domain/network-actions';
import { preferenceSearchConditionsState } from '@app/domain/user';
import { useSetSnackbar } from '@app/hooks/useSetSnackbar';
import { BOTTOM_MENU_ITEMS } from '@app/static/constants';
import {
  CategoryName,
  SearchCondition as SearchConditionProps,
} from '@app/types/catalog';
import { LoadableState } from '@app/types/common';
import { PreferenceKeys } from '@app/types/user';
import { getSearchResultUrl } from '@app/utils/catalog';
import { isError } from '@app/utils/error';
import { unescapeHtml } from '@app/utils/pattern';

export const orderByList = [
  {
    key: 'publicationDesc',
    label: '新着順',
    value: 'publication.since desc',
  },
  {
    key: 'hourlyPayDesc',
    label: '時給順',
    value: 'customFields.calculatedHourlyPay desc',
  },
  {
    key: 'dairyPayDesc',
    label: '日給順',
    value: 'customFields.calculatedDairyPay desc',
  },
  {
    key: 'addressAsc',
    label: '住所順',
    value: 'locationIds,customFields.workAddress1,customFields.workAddress2',
  },
  {
    key: 'dayAsc',
    label: '開始日付',
    value: 'customFields.day',
  },
];

export interface SearchResultRouteProps {
  favoriteSearch?: boolean;
}

export function Index(
  { favoriteSearch }: SearchResultRouteProps = { favoriteSearch: false }
): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const setSnackbar = useSetSnackbar();
  const authInfo = useRecoilValue(userAuthInfoSelector);
  const setPrefSearchConditionsState = useSetRecoilState(
    preferenceSearchConditionsState
  );
  const [conditionState, setConditionState] =
    useRecoilState(searchConditionState);
  const [resultProducts, setResultProducts] = useRecoilState(
    searchResultProductsState
  );
  const [loadable, setLoadable] = useState<LoadableState>(
    LoadableState.HAS_VALUE
  );
  const categories = useRecoilValue(categorySelector);
  const prefectures = useRecoilValue(locationsByPrefectureSelector);
  const attributesByClinicalDepartment = useRecoilValue(
    attributesByClinicalDepartmentSelector
  );
  const attributesByJobType = useRecoilValue(attributesByJobTypeSelector);

  const getParamToString = (value: string | null) => {
    return value ? value.split(',') : undefined;
  };

  const searchCondition = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return {
      attributeIds: getParamToString(queryParams.get('attributes')) || [],
      categoryId: queryParams.get('category') || '',
      keyword: queryParams.get('keyword') || '',
      locationIds: getParamToString(queryParams.get('locations')) || [],
      orderBy: queryParams.get('orderBy') || '',
      organizationId: queryParams.get('organizationId') || '',
      variant: {
        max: queryParams.get('variantMax') || '',
        min: queryParams.get('variantMin') || '',
        type: queryParams.get('variantType') || '',
      },
      work: {
        days: getParamToString(queryParams.get('days')) || [],
        timeFrom: queryParams.get('timeFrom') || '',
        timeTo: queryParams.get('timeTo') || '',
        weeks: getParamToString(queryParams.get('weeks')) || [],
      },
    };
  }, [location.search]);

  const headInfo = useMemo(() => {
    const category = categories?.find(
      (c) => c.id === searchCondition.categoryId
    )?.name;
    const prefecture =
      searchCondition.locationIds.length === 1
        ? prefectures?.find((p) => p.id === searchCondition.locationIds[0])
            ?.name
        : undefined;
    const attribute =
      searchCondition.attributeIds.length === 1
        ? attributesByClinicalDepartment?.find(
            (a) => a.id === searchCondition.attributeIds[0]
          ) ||
          attributesByJobType?.find(
            (a) => a.id === searchCondition.attributeIds[0]
          )
        : undefined;
    const paramLength = Array.from(
      new URLSearchParams(location.search).keys()
    ).length;

    if (
      !category ||
      paramLength > 2 ||
      (!prefecture && !attribute && paramLength > 1)
    ) {
      return {};
    }
    return {
      description: getProductsDescription(
        category,
        prefecture || attribute?.items[0]?.value || attribute?.name
      ),
      keywords: getProductsKeywords(
        category,
        prefecture || attribute?.customFields?.keywords
      ),
      title: getProductsTitle(
        category,
        prefecture || attribute?.items[0]?.value || attribute?.name
      ),
    };
  }, [
    attributesByClinicalDepartment,
    attributesByJobType,
    categories,
    location.search,
    prefectures,
    searchCondition,
  ]);

  const submitSearch = useCallback(
    (data?: SearchConditionProps) => {
      const conditions = { ...searchCondition, ...data };
      setConditionState(null);
      navigate(getSearchResultUrl(conditions));
    },
    [navigate, searchCondition, setConditionState]
  );

  const updateSearchCondition = useCallback(async () => {
    if (!authInfo) return;
    try {
      const newPreference = await postPreference(
        authInfo.userId,
        authInfo.accessToken,
        authInfo.fingerPrint,
        {
          key: PreferenceKeys.SEARCH_CONDITION,
          value: location.search,
        }
      );
      setPrefSearchConditionsState([
        {
          ...newPreference.data,
          value: unescapeHtml(newPreference.data.value),
        },
      ]);

      const result = await getPreferences(
        authInfo.userId,
        authInfo.accessToken,
        authInfo.fingerPrint,
        { key: PreferenceKeys.SEARCH_CONDITION }
      );
      for (const preference of result.data.value) {
        if (preference.id === newPreference.data.id) continue;
        await deletePreference(
          authInfo.userId,
          authInfo.accessToken,
          authInfo.fingerPrint,
          preference.id
        );
      }

      setSnackbar(true, '検索条件を保存しました', 'success');
    } catch (e) {
      if (isError(e)) {
        console.error(e.message);
      }
      setSnackbar(true, '検索条件の保存に失敗しました');
    }
  }, [authInfo, location.search, setSnackbar, setPrefSearchConditionsState]);

  const tabName = useMemo(() => {
    return (
      categories?.find((c) => c.id === searchCondition?.categoryId)?.name ||
      CategoryName.SPOT
    );
  }, [categories, searchCondition?.categoryId]);

  const orderByOptions = useMemo(() => {
    return orderByList.filter((o) => {
      if (tabName === CategoryName.PART_TIME) {
        return o.key !== 'dayAsc';
      }
      return true;
    });
  }, [tabName]);

  const fetchProducts = useCallback(
    async (nextLink?: string) => {
      if (loadable === LoadableState.LOADING) return;
      setLoadable(LoadableState.LOADING);
      try {
        const options = {
          ...searchCondition,
          categoryIds: searchCondition?.categoryId
            ? [searchCondition.categoryId]
            : [],
          expand: 'attributes,variants,images,organization,category,locations',
          nextLink,
          orderBy:
            orderByList.find((o) => o.key === searchCondition.orderBy)?.value ||
            orderByList[0].value,
        };
        // 定期非常勤の場合
        if (tabName === CategoryName.PART_TIME) {
          options.work.days = [];
        }

        const result = await getProducts(options);

        setConditionState(searchCondition);
        setResultProducts({
          data: nextLink
            ? [...resultProducts.data, ...result.data.value]
            : result.data.value,
          nextLink: result.data['@nextLink'],
          total: result.data.total,
        });
        setLoadable(LoadableState.HAS_VALUE);
      } catch (error) {
        setLoadable(LoadableState.HAS_ERROR);
        setSnackbar(true, '求人の取得に失敗しました');
      }
    },
    [
      loadable,
      searchCondition,
      resultProducts.data,
      setConditionState,
      setSnackbar,
      setResultProducts,
      tabName,
    ]
  );

  useEffect(() => {
    if (!conditionState) {
      setResultProducts({
        data: [],
        nextLink: '',
        scrollY: 0,
        total: 0,
      });
      void fetchProducts();
    } else {
      globalThis.scrollTo(0, resultProducts?.scrollY || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCondition]);

  const handleScrollThresholdReached = async () => {
    if (_.isEmpty(resultProducts.nextLink)) {
      return;
    }
    await fetchProducts(resultProducts.nextLink);
  };

  const onChangeTab = useCallback(
    (selectedTab: string) => {
      const categoryId =
        categories?.find((c) => c.name === selectedTab)?.id || '';

      let orderBy = searchCondition.orderBy;
      if (selectedTab === CategoryName.PART_TIME) {
        orderBy =
          orderBy === orderByList.find((v) => v.key === 'dayAsc')?.key
            ? orderByOptions[0].key
            : orderBy;
      }
      submitSearch({ ...searchCondition, categoryId, orderBy });
    },
    [categories, orderByOptions, searchCondition, submitSearch]
  );

  const handleClickProduct = useCallback(
    (id: string) => {
      setResultProducts({ ...resultProducts, scrollY: globalThis.pageYOffset });
      navigate(`/products/${id}`);
    },
    [navigate, resultProducts, setResultProducts]
  );

  return (
    <>
      <HeadBlock {...headInfo} />
      <Box data-e2e="searchResult-body">
        <Stack spacing={1} sx={{ bgcolor: 'secondary.main' }}>
          <Box sx={{ bgcolor: 'neutral.white' }}>
            {categories.length > 0 && (
              <SearchTypeTab
                items={categories}
                value={tabName}
                onChangeTab={onChangeTab}
              />
            )}
            <Suspense fallback={<Loading />}>
              <SearchCondition
                condition={searchCondition}
                onSearch={submitSearch}
              />
            </Suspense>
            <Container sx={{ py: 2 }}>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography>{resultProducts.total}件の検索結果</Typography>
                  {authInfo && (
                    <Button
                      variant="outlined"
                      color="black"
                      size="small"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={updateSearchCondition}
                    >
                      検索条件を保存
                    </Button>
                  )}
                </Stack>
                <FormControl size="small">
                  <Select
                    value={searchCondition.orderBy || orderByOptions[0].key}
                    onChange={(e) =>
                      submitSearch({
                        ...searchCondition,
                        orderBy: e.target.value,
                      })
                    }
                  >
                    {orderByOptions.map((item) => (
                      <MenuItem key={item.key} value={item.key}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Container>
          </Box>
          {loadable !== LoadableState.LOADING &&
            resultProducts.data.length === 0 && <SearchNoResult />}
          <Stack spacing={1} sx={{ bgcolor: 'secondary.main' }}>
            {loadable === LoadableState.LOADING && (
              <Loading title={`検索結果(${resultProducts.total})`} />
            )}
            {resultProducts.data.map((product, index) => (
              <ResultItemCard
                key={product.id + index}
                product={product}
                onClick={() => handleClickProduct(product.id)}
                favoriteSearch={favoriteSearch}
              />
            ))}
          </Stack>
          <ScrollThreshold
            disabled={_.isEmpty(resultProducts.nextLink)}
            thresholdReached={handleScrollThresholdReached}
          />
        </Stack>
        <BottomMenu menuItems={BOTTOM_MENU_ITEMS} />
      </Box>
    </>
  );
}
